import React, { useState, useEffect, useRef } from "react";
import { Button, IconButton, Input, Option, Select, Spinner } from "@material-tailwind/react";
import { Check, Edit2Icon, EyeIcon, ShieldAlert } from "lucide-react";
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import { extractCoordinates } from "../../utils/functions";
import { ModalX, PageHeader } from "../../components";
import { useNavigate } from "react-router-dom";
import { useGetBranches, useAddBranch, useUpdateBranch } from "../../custom-hooks/use-branches";
import { URLS } from "../../router-x/routes";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY || 'pk.eyJ1IjoiZmFycnVraHNoYWhpZCIsImEiOiJjbGhobnEwNWQwYWJuM2duems5cTdmZ2F5In0.VqxGVO_hETuJnKw4-ej3xA';

const PAKISTAN_CITIES = [
  { value: "lahore", label: "Lahore" },
  { value: "karachi", label: "Karachi" },
  { value: "islamabad", label: "Islamabad" },
  { value: "rawalpindi", label: "Rawalpindi" },
  { value: "faisalabad", label: "Faisalabad" },
  { value: "multan", label: "Multan" },
  { value: "peshawar", label: "Peshawar" },
  { value: "quetta", label: "Quetta" },
  { value: "hyderabad", label: "Hyderabad" },
  { value: "gujranwala", label: "Gujranwala" },
  { value: "gujrat", label: "Gujrat" },
  { value: "sialkot", label: "Sialkot" },
  { value: "sargodha", label: "Sargodha" },
  { value: "bahawalpur", label: "Bahawalpur" },
  { value: "abbottabad", label: "Abbottabad" },
  { value: "sheikhupura", label: "Sheikhupura" },
  { value: "sukkur", label: "Sukkur" },
  { value: "larkana", label: "Larkana" },
  { value: "murree", label: "Murree" },
  { value: "muzaffarabad", label: "Muzaffarabad" },
  { value: "mirpur", label: "Mirpur" },
  { value: "gwadar", label: "Gwadar" },
  { value: "mardan", label: "Mardan" },
  { value: "sahiwal", label: "Sahiwal" },
  { value: "okara", label: "Okara" },
  { value: "jhelum", label: "Jhelum" },
  { value: "kasur", label: "Kasur" },
  { value: "rahim_yar_khan", label: "Rahim Yar Khan" },
  { value: "dera_ghazi_khan", label: "Dera Ghazi Khan" },
];

const INITIAL_FORM_DATA = {
  _id: '',
  branchName: "",
  branchPhone: "",
  buildingCount: "NA",
  address: "",
  city: "lahore",
  state: "punjab",
  postalCode: "",
  country: "pakistan",
  googleMapsLink: "",
  lng: "",
  lat: "",
  email: "",
  website: ""
};

const BranchPage = () => {
  // React Router
  const navigate = useNavigate();

  // React Query hooks
  const { data: branches, isLoading: isBranchesLoading, error: branchesError } = useGetBranches();
  const addBranchMutation = useAddBranch();
  const updateBranchMutation = useUpdateBranch();

  // State variables
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [googleMapsLinkStatus, setGoogleMapsLinkStatus] = useState('');
  const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);

  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  // Initialize map on component mount
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-74.5, 40], // default center
      zoom: 9,
    });

    mapRef.current = map;

    return () => map.remove();
  }, []);

  // Update map markers when branches data changes
  useEffect(() => {
    if (!branches || branches.length === 0 || !mapRef.current) return;

    // Remove existing markers
    const markers = [];
    const existingMarkers = document.querySelectorAll('.mapboxgl-marker');
    existingMarkers.forEach(marker => marker.remove());

    // Add new markers and collect coordinates
    const coordinates = [];
    branches.forEach(branch => {
      if (branch.lng && branch.lat) {
        coordinates.push([branch.lng, branch.lat]);

        // Create and add marker
        const marker = new mapboxgl.Marker()
          .setLngLat([branch.lng, branch.lat])
          .setPopup(new mapboxgl.Popup().setHTML(
            `<h3>${branch.branchName}</h3><p>${branch.address}</p>`
          ))
          .addTo(mapRef.current);

        markers.push(marker);
      }
    });

    // If we have coordinates, fit the map to show all markers
    if (coordinates.length > 0) {
      const bounds = coordinates.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

      mapRef.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15,
        duration: 1000
      });
    }

    return () => {
      markers.forEach(marker => marker.remove());
    };
  }, [branches]);

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form input change
  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'googleMapsLink' && value !== "") {
      setIsLoadingCoordinates(true);
      const coords = await extractCoordinates(value);
      setIsLoadingCoordinates(false);
      if (coords.status === 200) {
        setGoogleMapsLinkStatus('Valid Link')
        setFormData({
          ...formData,
          [name]: value,
          lng: coords.longitude,
          lat: coords.latitude,
        })
      }
      else {
        setGoogleMapsLinkStatus('Invalid Location Link')
        setFormData({
          ...formData,
          lng: '',
          lat: '',
        })
      }
    }
  };

  // Reset form and modal state
  const resetForm = () => {
    setModalOpen(false);
    setFormData(INITIAL_FORM_DATA);
    setIsUpdating(false);
    setSelectedBranchId(null);
    setGoogleMapsLinkStatus('');
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (isUpdating) {
      // Update branch logic
      const updatedBranch = {
        name: formData.branchName,
        phone: formData.branchPhone,
        email: formData.email,
        website: formData.website,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postalCode,
        country: formData.country,
        location: {
          googleMapsLink: formData.googleMapsLink,
          lng: parseFloat(formData.lng),
          lat: parseFloat(formData.lat),
        }
      };

      updateBranchMutation.mutate({ id: selectedBranchId, data: updatedBranch });
      resetForm();
    } else {
      // Add new branch logic
      const newBranch = {
        name: formData.branchName,
        phone: formData.branchPhone,
        email: formData.email,
        website: formData.website,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postalCode,
        country: formData.country,
        location: {
          googleMapsLink: formData.googleMapsLink,
          lng: parseFloat(formData.lng),
          lat: parseFloat(formData.lat),
        }
      };

      addBranchMutation.mutate(newBranch);
      resetForm();
    }
  };

  // Open modal for adding a new branch
  const openAddBranchModal = () => {
    setModalOpen(true);
    setFormData(INITIAL_FORM_DATA);
    setIsUpdating(false);
  };

  // Open modal for updating a branch
  const openUpdateBranchModal = (branch) => {
    setModalOpen(true);
    setFormData({
      ...INITIAL_FORM_DATA,
      ...branch,
      city: branch.city || INITIAL_FORM_DATA.city,
      state: branch.state || INITIAL_FORM_DATA.state,
      country: branch.country || INITIAL_FORM_DATA.country,
      postalCode: branch.postalCode || "",
    });
    setIsUpdating(true);
    setSelectedBranchId(branch._id);
  };

  // Navigate to branch details page
  const navigateToBranchDetails = (branch) => {
    navigate(`${URLS.ADMIN.BRANCH}/${branch._id}`);
  };

  // Loading state for mutations
  const isSubmitting = addBranchMutation.isLoading || updateBranchMutation.isLoading;

  return (
    <div className={``}>
      <div className="m-5 p-5 border rounded-3xl">
        <PageHeader
          title="Branch Management"
          isLoading={isBranchesLoading}
          actionOnClick={openAddBranchModal}
          actionType="add"
          actionText="Add Branch"
        />
        <div className="mt-4">
          <div
            style={{ height: '40vh' }}
            ref={mapContainerRef}
            className="map-container rounded-3xl border"
          />
          {
            isBranchesLoading ? (
              <div className="flex justify-center py-5">
                <Spinner />
              </div>
            ) : branchesError ? (
              <div className="text-center py-5 text-red-500">
                Error loading branches. Please try again.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-max table-auto text-left">
                  <thead className="text-sm">
                    <tr className="py-3">
                      <th colSpan={3} className="py-3 text-xs font-light text-orange-900">
                        List of All Branches ({branches?.length || 0})
                      </th>
                    </tr>
                    <tr className="py-3 uppercase">
                      <th className="py-3">Name</th>
                      <th className="py-3">Building Count</th>
                      <th className="py-3">Contact</th>
                      <th className="py-3">Address</th>
                      <th className="py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branches?.map((branch, index) => (
                      <tr key={index} className="border-t hover:bg-gray-50 button rounded-full">
                        <td className="font-thin py-3 text-sm">
                          {branch.branchName}
                        </td>
                        <td className="font-thin py-3 text-sm">
                          {branch.buildingCount}
                        </td>
                        <td className="font-thin py-3 text-sm">
                          <div className={``}>
                            {branch.branchPhone}
                          </div>
                          <div className={``}>
                            {branch.email}
                          </div>
                        </td>
                        <td className="font-thin py-3 text-sm">
                          <div className={``}>
                            {branch.address}
                          </div>
                          <div className={``}>
                            {branch.city}, {branch.state} {branch.postalCode}, {branch.country}
                          </div>
                        </td>
                        <td className="flex gap-1 py-3">
                          <IconButton
                            size="sm"
                            color="blue"
                            className="rounded-full"
                            onClick={() => navigateToBranchDetails(branch)}
                          >
                            <EyeIcon size={12} />
                          </IconButton>
                          <IconButton
                            size="sm"
                            color="orange"
                            className="rounded-full"
                            onClick={() => openUpdateBranchModal(branch)}
                          >
                            <Edit2Icon size={12} />
                          </IconButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>

        {/* Modal for Add/Update Branch */}
        <ModalX
          isOpen={modalOpen}
          onClose={() => !isSubmitting && resetForm()}
          title={isUpdating ? "Update Branch" : "Add Branch"}
          maxWidth="xxl"
        >
          <form onSubmit={handleFormSubmit}>
            <div className="space-y-4">
              <div className="flex gap-4 flex-col md:flex-row">
                <Input
                  label="Branch Name"
                  name="branchName"
                  value={formData.branchName}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
                <Input
                  label="Branch Phone"
                  name="branchPhone"
                  value={formData.branchPhone}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex gap-4 flex-col md:flex-row">
                <Input
                  label="Branch Email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
                <Input
                  label="Website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </div>
              <div className="">
                <span className="text-md font-thin text-gray-900">
                  Address
                </span>
              </div>
              <div className="flex gap-4 flex-col md:flex-row">
                <Select
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={(value) => handleSelectChange("country", value)}
                  disabled={isSubmitting}
                >
                  <Option value="pakistan">Pakistan</Option>
                </Select>
                <Select
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={(value) => handleSelectChange("state", value)}
                  disabled={isSubmitting}
                >
                  <Option value="sindh">Sindh</Option>
                  <Option value="kpk">Khyber Pakhtunkhwa</Option>
                  <Option value="balochistan">Balochistan</Option>
                  <Option value="punjab">Punjab</Option>
                  <Option value="gilgit_baltistan">Gilgit-Baltistan</Option>
                  <Option value="ajk">Azad Jammu & Kashmir</Option>
                  <Option value="ict">Islamabad Capital Territory</Option>
                </Select>
                <Select
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={(value) => handleSelectChange("city", value)}
                  disabled={isSubmitting}
                >
                  {PAKISTAN_CITIES.map(({ value, label }) => (
                    <Option key={value} value={value}>{label}</Option>
                  ))}
                </Select>
              </div>
              <div className="flex w-full gap-4 flex-col md:flex-row">
                <div className="w-full md:w-4/6">
                  <Input
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="w-full md:w-2/6">
                  <Input
                    label="Postal Code"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div className="">
                <span className="text-md font-thin text-gray-900">
                  Location
                </span>
              </div>
              <div className="flex gap-4 flex-col md:flex-row">
                <Input
                  label="Google Maps Link"
                  name="googleMapsLink"
                  value={formData.googleMapsLink}
                  onChange={handleInputChange}
                  icon={formData.googleMapsLink !== "" ? (googleMapsLinkStatus ? <Check size={12} /> : <ShieldAlert size={12} />) : <></>}
                  disabled={isSubmitting}
                />
                <div className="flex gap-4">
                  <Input
                    label="Longitude"
                    name="lng"
                    value={formData.lng}
                    onChange={handleInputChange}
                    icon={isLoadingCoordinates ? <Spinner /> : <></>}
                    disabled={isSubmitting || isLoadingCoordinates}
                  />
                  <Input
                    label="Latitude"
                    name="lat"
                    value={formData.lat}
                    onChange={handleInputChange}
                    icon={isLoadingCoordinates ? <Spinner /> : <></>}
                    disabled={isSubmitting || isLoadingCoordinates}
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <Button
                onClick={resetForm}
                className="rounded-full bg-white text-black"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <Spinner size="sm" />
                    {isUpdating ? "Updating..." : "Adding..."}
                  </div>
                ) : (
                  isUpdating ? "Update" : "Add"
                )}
              </Button>
            </div>
          </form>
        </ModalX>
      </div>
    </div>
  );
};

export default BranchPage;